import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { RevisionLiability, RevisionLiabilityEditModel } from '../model/design-revision-reason';

const basePath = '/api/revision-liabilities';

/**
 * HTTP client for the revision liabilities. Listing is open to any authenticated user, because the QC
 * judging dialog and the data-insights views render from it; managing them requires
 * `write:design-revision-reason`. There is no delete — a liability is retired, never removed.
 */
@Injectable({ providedIn: 'root' })
export class RevisionLiabilityClient {
    private readonly _httpClient = inject(HttpClient);

    /**
     * @param includeRetired pass true when rendering historical data, or a judgement made under a
     * retired liability shows a blank label. Leave false when offering a new selection.
     */
    list(includeRetired: boolean): Observable<RevisionLiability[]> {
        const params = new HttpParams().set('includeRetired', String(includeRetired));
        return this._httpClient.get<RevisionLiability[]>(basePath, { params });
    }

    create(model: RevisionLiabilityEditModel): Observable<RevisionLiability> {
        return this._httpClient.post<RevisionLiability>(basePath, model);
    }

    update(id: number, model: RevisionLiabilityEditModel): Observable<RevisionLiability> {
        return this._httpClient.put<RevisionLiability>(`${basePath}/${id}`, model);
    }

    /** Retires (`isActive` false) or restores a liability. */
    setActive(id: number, isActive: boolean): Observable<RevisionLiability> {
        return this._httpClient.patch<RevisionLiability>(`${basePath}/${id}/active`, isActive);
    }
}
